import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { getAllProperties } from "@/lib/api/api"
import { getAvailableProperties } from "@/lib/api/workspace-team"
import { useAuth } from "@/lib/contexts/AuthContext"
import type { TrackingStatus } from "@/lib/types/workspace-team"

type OnboardingProgressStepId = "add-site" | "install-script" | "launch-test"

type OnboardingProgressStep = {
  id: OnboardingProgressStepId
  n: number
  label: string
  done: boolean
}

type OnboardingProperty = {
  propertyId: string
  name?: string
  domain: string
  scriptId?: string
  trackingStatus?: TrackingStatus
}

type RawProperty = {
  propertyId?: string
  property_id?: string
  name?: string
  domain?: string
  scriptId?: string
  script_id?: string
  ownerScriptId?: string
  archived?: boolean
  isDemo?: boolean
  is_demo?: boolean
  isStaging?: boolean
  is_staging?: boolean
  trackingStatus?: TrackingStatus
  tracking_status?: TrackingStatus
  stats?: { views?: number; events?: number }
  subscription?: {
    currentViews?: number
    currentEvents?: number
  }
}

type OnboardingProgressData = {
  steps: OnboardingProgressStep[]
  currentStep: number
  isComplete: boolean
  realSiteCount: number
  firstProperty: OnboardingProperty | null
}

/**
 * The canonical onboarding spine. Single source of truth for every surface that
 * renders a stepper: the wizard and the dashboard gate.
 */
const ONBOARDING_STEP_LABELS = [
  "Add site",
  "Connect tracker",
  "Launch first test",
] as const

const EMPTY_PROGRESS: OnboardingProgressData = {
  steps: [
    { id: "add-site", n: 1, label: ONBOARDING_STEP_LABELS[0], done: false },
    { id: "install-script", n: 2, label: ONBOARDING_STEP_LABELS[1], done: false },
    { id: "launch-test", n: 3, label: ONBOARDING_STEP_LABELS[2], done: false },
  ],
  currentStep: 1,
  isComplete: false,
  realSiteCount: 0,
  firstProperty: null,
}

function propertyIdOf(property: RawProperty): string {
  return property.propertyId ?? property.property_id ?? ""
}

function hasReceivedData(property: RawProperty): boolean {
  const views = property.stats?.views ?? property.subscription?.currentViews ?? 0
  const events = property.stats?.events ?? property.subscription?.currentEvents ?? 0
  return views > 0 || events > 0
}

function isRealProperty(property: RawProperty): boolean {
  return Boolean(propertyIdOf(property)) && !property.archived && !property.isDemo && !property.is_demo
}

function buildSteps(hasRealSite: boolean, hasVerifiedTracking: boolean): OnboardingProgressStep[] {
  return [
    { id: "add-site", n: 1, label: ONBOARDING_STEP_LABELS[0], done: hasRealSite },
    { id: "install-script", n: 2, label: ONBOARDING_STEP_LABELS[1], done: hasVerifiedTracking },
    // This is intentionally non-gating; it stays incomplete until the user opts in.
    { id: "launch-test", n: 3, label: ONBOARDING_STEP_LABELS[2], done: false },
  ]
}

export function useOnboardingProgress() {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ["onboarding-progress", token],
    queryFn: async (): Promise<OnboardingProgressData> => {
      if (!token) return EMPTY_PROGRESS

      const [propertiesRes, availableRes] = await Promise.all([
        getAllProperties(token),
        getAvailableProperties(token),
      ])

      const properties = ((propertiesRes as { properties?: RawProperty[] })?.properties ?? [])
        .filter(isRealProperty)
      const availableById = new Map(
        (availableRes.properties ?? []).map((property) => [property.property_id, property])
      )

      const firstRaw = properties[0]
      const hasRealSite = properties.length > 0
      const hasVerifiedTracking = properties.some((property) => {
        const propertyId = propertyIdOf(property)
        const available = availableById.get(propertyId)
        const trackingStatus = available?.tracking_status ?? property.tracking_status ?? property.trackingStatus
        return trackingStatus === "verified" || hasReceivedData(property)
      })

      const steps = buildSteps(hasRealSite, hasVerifiedTracking)
      const firstIncomplete = steps.find((step) => !step.done)

      return {
        steps,
        currentStep: firstIncomplete?.n ?? 3,
        isComplete: hasRealSite && hasVerifiedTracking,
        realSiteCount: properties.length,
        firstProperty: firstRaw
          ? {
              propertyId: propertyIdOf(firstRaw),
              name: firstRaw.name,
              domain: firstRaw.domain ?? "",
              scriptId: firstRaw.scriptId ?? firstRaw.script_id ?? firstRaw.ownerScriptId,
              trackingStatus:
                availableById.get(propertyIdOf(firstRaw))?.tracking_status ??
                firstRaw.tracking_status ??
                firstRaw.trackingStatus,
            }
          : null,
      }
    },
    enabled: !!token,
    // 30s, not 0: with staleTime 0 every remount of the hub refetched, and the
    // gate used to key off isFetching, so the onboarding card flashed on every
    // dashboard visit. Kept short because OnboardingGate calls refetch() and
    // depends on prompt invalidation after a site is added.
    staleTime: 30_000,
    // Hold the last good value through a refetch so realSiteCount never dips
    // back to EMPTY_PROGRESS's 0 mid-flight.
    placeholderData: (previousData) => previousData,
    retry: false,
  })

  const data = query.data ?? EMPTY_PROGRESS

  return useMemo(
    () => ({
      ...data,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: query.refetch,
    }),
    [data, query.isFetching, query.isLoading, query.refetch]
  )
}
